import { useEffect, useMemo, useState } from "react";
import { Shell, Card } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { FeesTable, PayState } from "../components/Billing";
import { PauseCard } from "../components/PauseCard";
import { WorksheetList } from "../components/Worksheets";
import { apiError, http, SEDES, ANAMNESIS_FIELDS } from "../lib/api";

export default function PerfilLogopedia() {
  const [patients, setPatients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [anam, setAnam] = useState(null);
  const [active, setActive] = useState(null);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [confirmBaja, setConfirmBaja] = useState(null);
  const [showNext, setShowNext] = useState(false);

  const load = async () => {
    const [p, s] = await Promise.all([
      http.get("/logopedia/patients"), http.get("/logopedia/sessions"),
    ]);
    setPatients(p.data);
    setSessions(s.data);
    setActive((cur) => cur || p.data[0]?.id || null);
  };

  useEffect(() => { load(); }, []);

  const patient = useMemo(() => patients.find((p) => p.id === active), [patients, active]);

  useEffect(() => {
    if (!active) return;
    http.get("/logopedia/worksheets", { params: { patient_id: active } })
      .then(({ data }) => setSheets(data));
    http.get(`/logopedia/anamnesis/${active}`)
      .then(({ data }) => setAnam(data?.data || {}))
      .catch(() => setAnam({}));
  }, [active]);

  const act = async (fn, okMsg) => {
    setMsg(""); setError("");
    try {
      const res = await fn();
      setMsg(res?.data?.message || okMsg);
      await load();
      if (active) {
        const { data } = await http.get("/logopedia/worksheets", { params: { patient_id: active } });
        setSheets(data);
      }
    } catch (err) { setError(apiError(err)); }
  };

  if (patients.length === 0)
    return (
      <Shell accent="logo" title="Logopedia" testid="perfil-logo-page">
        <Card className="max-w-2xl">
          <p className="text-lg">Actualmente no consta ningún paciente registrado en Logopedia.</p>
          <Button data-testid="logo-add-patient-btn" className="mt-6 h-14 bg-logo text-lg hover:bg-logo-hover"
            onClick={() => (window.location.href = "/logopedia")}>
            Registrar un paciente
          </Button>
        </Card>
      </Shell>
    );

  const mySessions = sessions.filter((s) => s.patient_id === active);
  const upcoming = mySessions.filter((s) => s.status === "reservada"
    && new Date(`${s.date}T${s.start}:00`) > new Date());

  return (
    <Shell accent="logo" title="Mi área de Logopedia"
      subtitle="Consulte su bono, cambie sesiones, revise sus fichas de trabajo y sus pagos."
      testid="perfil-logo-page">
      <div className="mb-6 flex flex-wrap gap-3">
        {patients.map((p) => (
          <Button key={p.id} data-testid={`patient-tab-${p.id}`} onClick={() => setActive(p.id)}
            className={`h-14 text-lg ${active === p.id ? "bg-logo hover:bg-logo-hover" : "bg-white text-ink-900 hover:bg-logo-light"}`}>
            {p.name}
          </Button>
        ))}
      </div>

      {msg && <p data-testid="logo-success" className="mb-5 rounded-xl bg-logo-light p-4 text-lg text-logo-text">{msg}</p>}
      {error && <p data-testid="logo-error" className="mb-5 rounded-xl bg-red-50 p-4 text-lg text-red-700">{error}</p>}

      {patient && (
        <>
          {/* 1. RESUMEN VISUAL DEL BONO */}
          <Card className="mb-8" data-testid="bono-box">
            <h2 className="font-head text-2xl font-medium">{patient.name} · sede de {SEDES[patient.sede]}</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl bg-cream p-5" data-testid="summary-schedule">
                <p className="text-base text-ink-700">Horario habitual</p>
                <p className="mt-1 font-head text-xl font-medium">
                  {patient.fixed_schedule?.length
                    ? patient.fixed_schedule.map((f) => `${f.day_name} · ${f.start}`).join(" / ")
                    : "pendiente de asignar"}
                </p>
              </div>
              <div className="rounded-xl bg-logo-light p-5" data-testid="summary-bono">
                <p className="text-base text-logo-text">Bono {patient.bono_number}</p>
                <p className="mt-1 font-head text-xl font-medium">{patient.bono_total} sesiones</p>
              </div>
              <div className="rounded-xl bg-white p-5 ring-1 ring-border" data-testid="summary-done">
                <p className="text-base text-ink-700">Realizadas</p>
                <p className="mt-1 font-head text-3xl font-medium" data-testid="bono-used">
                  {patient.bono_used}/{patient.bono_total}
                </p>
              </div>
              <div className="rounded-xl bg-white p-5 ring-1 ring-border" data-testid="summary-left">
                <p className="text-base text-ink-700">Restantes</p>
                <p className="mt-1 font-head text-3xl font-medium" data-testid="bono-left">{patient.bono_left}</p>
              </div>
            </div>
            {(patient.absences > 0 || patient.to_recover > 0) && (
              <p className="mt-4 rounded-xl bg-amber-50 p-4 text-base text-amber-900" data-testid="summary-absences">
                {patient.absences > 0 && <>Ausencias registradas: <strong>{patient.absences}</strong>. </>}
                {patient.to_recover > 0 && <>Sesiones pendientes de recuperar: <strong>{patient.to_recover}</strong>. </>}
                El equipo de SISU se encarga de organizar las recuperaciones.
              </p>
            )}
            <p className="mt-4 text-base text-ink-700">
              Las sesiones realizadas las registra el equipo de SISU tras cada visita. Su horario
              queda reservado todas las semanas; si necesita cambiarlo de forma definitiva,
              comuníquelo al equipo.
            </p>

            <h3 className="mt-8 font-head text-xl font-medium">Historial de bonos</h3>
            <div className="mt-3 -mx-2 overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-base" data-testid="bonos-table">
                <thead>
                  <tr className="border-b border-border text-ink-700">
                    <th className="py-2 pr-3">Bono</th>
                    <th className="py-2 pr-3">Sesiones</th>
                    <th className="py-2 pr-3">Importe</th>
                    <th className="py-2 pr-3">Inicio</th>
                    <th className="py-2 pr-3">Finalización</th>
                    <th className="py-2 pr-3">Abonado</th>
                    <th className="py-2 pr-3">Pendiente</th>
                    <th className="py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {patient.bonos.map((b) => (
                    <tr key={b.id} className={`border-b border-border ${b.active ? "bg-cream" : ""}`}
                      data-testid={`bono-row-${b.number}`}>
                      <td className="py-2 pr-3">Bono {b.number}{b.active ? " (en curso)" : ""}</td>
                      <td className="py-2 pr-3">{b.sessions}</td>
                      <td className="py-2 pr-3">{b.price.toFixed(2)} €</td>
                      <td className="py-2 pr-3">{b.start_date}</td>
                      <td className="py-2 pr-3">{b.end_date || "—"}</td>
                      <td className="py-2 pr-3">{b.paid.toFixed(2)} €</td>
                      <td className="py-2 pr-3">{b.pending.toFixed(2)} €</td>
                      <td className="py-2"><PayState status={b.status} testid={`bono-state-${b.number}`} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* 2. CAMBIO DE SESIÓN Y DE BONO */}
          <Card className="mb-8" data-testid="section-cambio-sesion">
            <h2 className="font-head text-2xl font-medium">Cambios</h2>
            <p className="mt-2 text-base text-ink-700">
              Si un día concreto no puede acudir, avise con un mínimo de <strong>6 horas</strong>;
              con menos de 6 horas la sesión se descuenta del bono. La cancelación afecta solo a ese
              día: su horario habitual se mantiene.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="text-base">Cambiar el tipo de bono:</span>
              {[4, 8].map((n) => (
                <Button key={n} data-testid={`family-bono-${n}`} variant="outline"
                  className={`h-12 text-base ${patient.bono_total === n ? "border-logo bg-logo-light" : ""}`}
                  onClick={() => act(() => http.put(
                    `/logopedia/bonos/${patient.bonos.find((b) => b.active)?.id}/sessions`,
                    { sessions: n }), `Bono cambiado a ${n} sesiones.`)}>
                  Bono de {n} sesiones
                </Button>
              ))}
            </div>

            <Button data-testid="toggle-next-sessions" variant="outline" className="mt-5 h-12 text-base"
              onClick={() => setShowNext(!showNext)}>
              {showNext ? "Ocultar mis próximos días" : "No puedo acudir un día"}
            </Button>
            {showNext && (
              <ul className="mt-4 space-y-3" data-testid="sessions-list">
                {upcoming.length === 0 && <li className="text-lg text-ink-700">No hay días reservados próximamente.</li>}
                {upcoming.slice(0, 4).map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-4"
                    data-testid={`session-${s.id}`}>
                    <span className="text-lg">{s.date} a las {s.start}</span>
                    <Button data-testid={`cancel-session-${s.id}`} variant="outline" className="h-12 text-base"
                      onClick={() => act(() => http.delete(`/logopedia/sessions/${s.id}`), "Sesión actualizada.")}>
                      No puedo acudir ese día
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            {patient.bono_left <= 0 && (
              <p className="mt-5 rounded-xl bg-centro-light p-4 text-base text-centro-text">
                No quedan sesiones disponibles en el bono. El equipo de SISU registrará el bono
                siguiente manteniendo su horario; el abono se realiza en el centro.
              </p>
            )}
          </Card>

          {/* 2b. FICHAS DE TRABAJO */}
          <Card className="mb-8" data-testid="section-fichas">
            <h2 className="font-head text-2xl font-medium">Fichas de trabajo</h2>
            <p className="mt-2 text-base text-ink-700">
              Marque cada ficha como completada o no completada y añada sus observaciones: el equipo
              de logopedia las verá en su panel. Se conservan todas las fichas anteriores.
            </p>
            <WorksheetList sheets={sheets}
              onSave={(id, body) => act(() => http.put(`/logopedia/worksheets/${id}`, body),
                "Ficha actualizada.")} />
          </Card>

          {/* 3. PROFESIONALES EXTERNOS */}
          <Card className="mb-8" data-testid="section-profesionales">
            <h2 className="font-head text-2xl font-medium">Profesionales externos e informes</h2>
            <p className="mt-2 text-base text-ink-700">
              Colegio, Audición y Lenguaje, dentista u ortodoncista, adaptaciones, informes y sus
              comentarios. Puede actualizar esta información cuando lo necesite; solo la ve el
              equipo de SISU.
            </p>
            <div className="mt-4 space-y-5">
              {ANAMNESIS_FIELDS.map(([key, label]) => (
                <div key={key}>
                  <Label htmlFor={`an-${key}`} className="text-lg">{label}</Label>
                  <Textarea id={`an-${key}`} data-testid={`anam-${key}-input`} rows={3}
                    className="mt-2 text-base" value={(anam || {})[key] || ""}
                    onChange={(e) => setAnam({ ...(anam || {}), [key]: e.target.value })} />
                </div>
              ))}
            </div>
            <Button data-testid="save-anamnesis-btn" className="mt-4 h-14 bg-logo text-lg hover:bg-logo-hover"
              onClick={() => act(() => http.post("/logopedia/anamnesis", {
                patient_id: patient.id, data: anam || {},
              }), "Información actualizada.")}>
              Guardar la información
            </Button>
          </Card>

          {/* 4. CUOTAS Y PAGOS */}
          <Card className="mb-8" data-testid="section-pagos-logo">
            <h2 className="font-head text-2xl font-medium">Cuotas y pagos</h2>
            <p className="mt-2 text-lg text-ink-700">
              Año completo, de enero a diciembre. Cada bono se registra en el mes en el que comienza;
              el equipo de SISU anota las cantidades abonadas y lo pendiente se calcula solo.
            </p>
            <div className="mt-5">
              <FeesTable rows={patient.payments} totalDebt={patient.total_debt}
                testid="logo-fees-table"
                note="El abono se realiza en el centro, no a través de la web." />
            </div>
          </Card>

          {/* 5. PAUSAR UN MES */}
          <PauseCard name={patient.name} pauseMonth={patient.pause_month} accent="logo"
            testid="pause-patient"
            onPause={(month) => act(() => http.post(`/logopedia/patients/${patient.id}/pause`, { month }),
              "Pausa registrada. La cuenta se reactivará automáticamente al mes siguiente.")}
            onCancel={() => act(() => http.delete(`/logopedia/patients/${patient.id}/pause`),
              "Pausa anulada.")} />

          {/* 6. DARSE DE BAJA */}
          <Card className="mb-8 border-red-200" data-testid="section-baja-logo">
            <h2 className="font-head text-2xl font-medium">Dar de baja a {patient.name}</h2>
            <p className="mt-2 text-lg text-ink-700">
              Al dar de baja se eliminarán de la web todos los datos de {patient.name} (sesiones,
              bonos, fichas y anamnesis) y sus sesiones quedarán libres de inmediato. Esta acción no
              se puede deshacer. Si solo necesita interrumpir un mes, utilice la pausa temporal.
            </p>
            {confirmBaja === patient.id ? (
              <div className="mt-4 flex flex-wrap gap-3">
                <Button data-testid="confirm-baja-patient-btn" className="h-14 bg-red-700 text-lg hover:bg-red-800"
                  onClick={() => act(async () => {
                    await http.delete(`/logopedia/patients/${patient.id}`);
                    setActive(null);
                    setConfirmBaja(null);
                  }, `${patient.name} ha sido dado/a de baja y sus datos se han eliminado.`)}>
                  Sí, dar de baja definitivamente
                </Button>
                <Button data-testid="cancel-baja-patient-btn" variant="outline" className="h-14 text-lg"
                  onClick={() => setConfirmBaja(null)}>
                  No, mantener el expediente
                </Button>
              </div>
            ) : (
              <Button data-testid="baja-patient-btn" variant="outline" className="mt-4 h-14 text-lg"
                onClick={() => setConfirmBaja(patient.id)}>
                Dar de baja a este paciente
              </Button>
            )}
          </Card>
        </>
      )}
    </Shell>
  );
}
